import { useContext } from 'react';
import { ClientContext, type ClientContextType } from '@/contexts/ClientContext';

/**
 * Hook to access the Client context
 * Must be used within a ClientProvider
 *
 * @returns ClientContextType containing:
 * - selectedClient: Currently selected client
 * - availableClients: List of all available clients
 * - setSelectedClient: Function to change the selected client
 * - isLoadingClients: Loading state for clients fetch
 * - clientsError: Error state if clients fetch fails
 * - refetchClients: Function to manually refetch clients
 *
 * @throws Error if used outside of ClientProvider
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { selectedClient, setSelectedClient, availableClients } = useClient();
 *
 *   return (
 *     <div>
 *       <p>Current client: {selectedClient?.name}</p>
 *       <select onChange={(e) => {
 *         const client = availableClients.find(c => c.id === e.target.value);
 *         setSelectedClient(client || null);
 *       }}>
 *         {availableClients.map(client => (
 *           <option key={client.id} value={client.id}>{client.name}</option>
 *         ))}
 *       </select>
 *     </div>
 *   );
 * }
 * ```
 */
export function useClient(): ClientContextType {
  const context = useContext(ClientContext);
  if (context === undefined) {
    throw new Error('useClient must be used within a ClientProvider');
  }
  return context;
}
